import { type SetStateAction, useCallback, useEffect, useRef } from "react";

import { useHistoryTrackerStore } from "./history-tracker.store";

export const useHistoryTracker = () => {
  const previousUrl = useHistoryTrackerStore((state) => state.entries.find((entry) => entry.id === state.previousEntryId)?.url);
  const currentUrl = useHistoryTrackerStore((state) => state.url);

  return {
    previousUrl,
    currentUrl,
  };
};

export const useHistoryTrackerState = <T>(
  key: string,
  defaultValue: T,
): [T, (v: SetStateAction<T>) => void, (v: SetStateAction<T>) => void, () => T] => {
  const defaultValueRef = useRef(defaultValue);

  const state = useHistoryTrackerStore((state) => state.state) as Record<string, unknown> | null;
  const setState = useHistoryTrackerStore((state) => state.setState);
  const pushState = useHistoryTrackerStore((state) => state.pushState);
  const peekState = useHistoryTrackerStore((state) => state.peekState);

  const set = useCallback(
    (valueOrFn: SetStateAction<T>) => {
      setState((prevState) => {
        if (!(valueOrFn instanceof Function)) {
          return {
            [key]: valueOrFn,
          };
        }

        const prevStateForKey = (prevState[key] as T) ?? defaultValueRef.current;
        return {
          [key]: valueOrFn(prevStateForKey),
        };
      });
    },
    [setState, key],
  );

  const push = useCallback(
    (valueOrFn: SetStateAction<T>) => {
      pushState((prevState) => {
        if (!(valueOrFn instanceof Function)) {
          return {
            [key]: valueOrFn,
          };
        }

        const prevStateForKey = (prevState[key] as T) ?? defaultValueRef.current;
        return {
          [key]: valueOrFn(prevStateForKey),
        };
      });
    },
    [pushState, key],
  );

  const peek = useCallback(() => {
    const fullState = peekState();

    return (fullState[key] as T) ?? defaultValueRef.current;
  }, [peekState, key]);

  return [(state?.[key] as T) ?? defaultValue, set, push, peek];
};

export const useHistoryTrackerDistanceCalculator = <T>(key: string, defaultValue: T) => {
  const defaultValueRef = useRef(defaultValue);
  const calculateDistanceToState = useHistoryTrackerStore((state) => state.calculateDistanceToState);

  useEffect(() => {
    defaultValueRef.current = defaultValue;
  }, [defaultValue]);

  return useCallback(
    (matcher: (v: T) => boolean) =>
      calculateDistanceToState((state) => {
        if (!state) {
          return false;
        }

        return matcher(((state as Record<string, unknown>)?.[key] as T) ?? defaultValueRef.current);
      }),
    [calculateDistanceToState, key],
  );
};
